import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useLingui } from "@lingui/react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ImageUpload } from "@/components/ui/image-upload";
import { ActivityType } from "@/types/activities";
import {
  ActivityFormData,
  ActivityFormInput,
} from "@/schemas/activityPayloadSchemas";
import { CheckOutPickupFields } from "./CheckOutPickupFields";
import { images_GenerateUploadURLRequest } from "@/types/image";
import { useImageUrl } from "@/hooks/useImage";
import { isImageId } from "@/utils/imageUtils";

/**
 * The form schema types `data` as `Record<string, unknown>` (the payload schema
 * is chosen dynamically per activity type), so every `data.*` field resolves to
 * `unknown` and cannot go straight into a DOM `value` prop.
 *
 * Runtime-equivalent to the `field.value ?? ""` this replaced: nullish becomes
 * "", and `String()` on the strings these fields actually hold returns the very
 * same string.
 */
function asText(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

interface ActivityTypeFormFieldsProps {
  type: string;
  form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData>;
}

/**
 * Activity Type Form Fields Component
 *
 * Renders type-specific form fields based on the selected activity type
 * Supports all 19 activity types with proper validation
 */
export function ActivityTypeFormFields({
  type,
  form,
}: ActivityTypeFormFieldsProps) {
  switch (type) {
    case ActivityType.ActivityTypeCheckIn:
      return <CheckInFields form={form} />;

    case ActivityType.ActivityTypeCheckOut:
      return <CheckOutFields form={form} />;

    case ActivityType.ActivityTypeToiletPee:
    case ActivityType.ActivityTypeToiletPoo:
    case ActivityType.ActivityTypeToiletNothing:
      return <ToiletFields form={form} />;

    case ActivityType.ActivityTypeFeedingMilk:
    case ActivityType.ActivityTypeFeedingPorridge:
    case ActivityType.ActivityTypeFeedingSolid:
    case ActivityType.ActivityTypeFeedingSnack:
    case ActivityType.ActivityTypeFeedingBreastmilk:
    case ActivityType.ActivityTypeFeedingRefused:
      return <FeedingFields form={form} feedingType={type} />;

    case ActivityType.ActivityTypeNapStart:
    case ActivityType.ActivityTypeNapEnd:
      return <NapFields form={form} />;

    case ActivityType.ActivityTypeHealthAccident:
      return <AccidentFields form={form} />;

    case ActivityType.ActivityTypeHealthIllness:
      return <IllnessFields form={form} />;

    case ActivityType.ActivityTypeHealthMedication:
      return <MedicationFields form={form} />;

    case ActivityType.ActivityTypePicture:
      return <PictureFields form={form} />;

    case ActivityType.ActivityTypeNote:
      return <NoteFields form={form} />;

    case ActivityType.ActivityTypeMoodCheck:
      return <MoodCheckFields form={form} />;

    default:
      return null;
  }
}

// ============================================================================
// Type-Specific Field Components
// ============================================================================

function CheckInFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="data.mood"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.mood", message: "Mood" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectMood", message: "Select mood" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="happy">{i18n._({ id: "activities.mood.happy", message: "Happy" })}</SelectItem>
                <SelectItem value="ok">{i18n._({ id: "activities.mood.ok", message: "OK" })}</SelectItem>
                <SelectItem value="unhappy">{i18n._({ id: "activities.mood.unhappy", message: "Unhappy" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.notes", message: "Notes" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function CheckOutFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();
  const childId = form.watch("childId");

  return (
    <div className="space-y-4">
      {/* The collector record is mandatory and two-pathed (issue #993). The old
          single `picked_up_by` contact picker is gone: it wrote a bare contact
          id nothing validated, and a check-out without a recorded collector is
          exactly the safeguarding gap #993 closes. */}
      <CheckOutPickupFields form={form} childId={childId} />

      <FormField
        control={form.control}
        name="data.mood"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.mood", message: "Mood" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectMood", message: "Select mood" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="happy">{i18n._({ id: "activities.mood.happy", message: "Happy" })}</SelectItem>
                <SelectItem value="ok">{i18n._({ id: "activities.mood.ok", message: "OK" })}</SelectItem>
                <SelectItem value="unhappy">{i18n._({ id: "activities.mood.unhappy", message: "Unhappy" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function ToiletFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <FormField
      control={form.control}
      name="data.notes"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{i18n._({ id: "activities.form.notes", message: "Notes" })}</FormLabel>
          <FormControl>
            <Textarea {...field} value={asText(field.value)} rows={3} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// Feeding types that use ml quantity (fluid types)
const ML_QUANTITY_TYPES = [
  ActivityType.ActivityTypeFeedingMilk,
  ActivityType.ActivityTypeFeedingBreastmilk,
  ActivityType.ActivityTypeFeedingPorridge,
];

// Feeding types that use quantity comment (solid types)
const COMMENT_QUANTITY_TYPES = [
  ActivityType.ActivityTypeFeedingSolid,
  ActivityType.ActivityTypeFeedingSnack,
];

// feeding_refused shows neither quantity field

function FeedingFields({ form, feedingType }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData>; feedingType: string }) {
  const { i18n } = useLingui();

  const showMlQuantity = ML_QUANTITY_TYPES.includes(feedingType as typeof ActivityType.ActivityTypeFeedingMilk);
  const showCommentQuantity = COMMENT_QUANTITY_TYPES.includes(feedingType as typeof ActivityType.ActivityTypeFeedingSolid);

  return (
    <div className="space-y-4">
      {/* Quantity ML - shown for fluid types (milk, breastmilk, porridge) */}
      {showMlQuantity && (
        <FormField
          control={form.control}
          name="data.quantity_ml"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{i18n._({ id: "activities.form.quantityMl", message: "Quantity (ml)" })}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  min="0"
                  max="2000"
                  step="10"
                  value={asText(field.value)}
                  placeholder={i18n._({ id: "activities.form.quantityMlPlaceholder", message: "e.g. 200" })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Quantity Comment - shown for solid types (solid food, snack) */}
      {showCommentQuantity && (
        <FormField
          control={form.control}
          name="data.quantity_comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{i18n._({ id: "activities.form.quantityComment", message: "Quantity" })}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value as string}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n._({ id: "activities.form.selectQuantity", message: "Select quantity" })} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="few">{i18n._({ id: "activities.quantity.few", message: "Little" })}</SelectItem>
                  <SelectItem value="ok">{i18n._({ id: "activities.quantity.ok", message: "OK" })}</SelectItem>
                  <SelectItem value="much">{i18n._({ id: "activities.quantity.much", message: "A lot" })}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

function NapFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <FormField
      control={form.control}
      name="data.notes"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{i18n._({ id: "activities.form.notes", message: "Notes" })}</FormLabel>
          <FormControl>
            <Textarea {...field} value={asText(field.value)} rows={3} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function AccidentFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="data.severity"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.severity", message: "Severity" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectSeverity", message: "Select severity" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="minor">{i18n._({ id: "activities.severity.minor", message: "Minor" })}</SelectItem>
                <SelectItem value="moderate">{i18n._({ id: "activities.severity.moderate", message: "Moderate" })}</SelectItem>
                <SelectItem value="serious">{i18n._({ id: "activities.severity.serious", message: "Serious" })}</SelectItem>
                <SelectItem value="critical">{i18n._({ id: "activities.severity.critical", message: "Critical" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.course_of_accident"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.courseOfAccident", message: "Course of Accident" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.incidents"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.incidents", message: "Incidents" })}</FormLabel>
            <FormControl>
              <Textarea
                value={Array.isArray(field.value) ? (field.value as string[]).join("\n") : ""}
                onChange={(e) => {
                  const lines = e.target.value.split("\n").filter(line => line.trim() !== "");
                  field.onChange(lines.length > 0 ? lines : e.target.value === "" ? [] : [e.target.value]);
                }}
                onBlur={() => {
                  // Clean up on blur - filter empty lines
                  if (Array.isArray(field.value)) {
                    const cleaned = (field.value as string[]).filter(v => v.trim() !== "");
                    field.onChange(cleaned);
                  }
                }}
                rows={3}
                placeholder={i18n._({ id: "activities.form.incidentsPlaceholder", message: "Enter each incident on a new line" })}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.measures"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.measuresTaken", message: "Measures Taken" })}</FormLabel>
            <FormControl>
              <Textarea
                value={Array.isArray(field.value) ? (field.value as string[]).join("\n") : ""}
                onChange={(e) => {
                  const lines = e.target.value.split("\n").filter(line => line.trim() !== "");
                  field.onChange(lines.length > 0 ? lines : e.target.value === "" ? [] : [e.target.value]);
                }}
                onBlur={() => {
                  // Clean up on blur - filter empty lines
                  if (Array.isArray(field.value)) {
                    const cleaned = (field.value as string[]).filter(v => v.trim() !== "");
                    field.onChange(cleaned);
                  }
                }}
                rows={3}
                placeholder={i18n._({ id: "activities.form.measuresPlaceholder", message: "Enter each measure on a new line" })}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.place_of_accident"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.placeOfAccident", message: "Place of Accident" })}</FormLabel>
            <FormControl>
              <Input {...field} value={asText(field.value)} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.observer"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.observer", message: "Observer" })}</FormLabel>
            <FormControl>
              <Input {...field} value={asText(field.value)} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.perpetrator"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.perpetrator", message: "Perpetrator" })}</FormLabel>
            <FormControl>
              <Input {...field} value={asText(field.value)} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.prevention_measures"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.preventionMeasures", message: "Prevention Measures" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} placeholder={i18n._({ id: "activities.form.preventionMeasuresPlaceholder", message: "What can be done to prevent this in the future?" })} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.mood"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.mood", message: "Mood" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectMood", message: "Select mood" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="happy">{i18n._({ id: "activities.mood.happy", message: "Happy" })}</SelectItem>
                <SelectItem value="ok">{i18n._({ id: "activities.mood.ok", message: "OK" })}</SelectItem>
                <SelectItem value="unhappy">{i18n._({ id: "activities.mood.unhappy", message: "Unhappy" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.child_to_be_picked_up"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{i18n._({ id: "activities.form.childToBePickedUp", message: "Child To Be Picked Up" })}</FormLabel>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.notes", message: "Notes" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function IllnessFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="data.illnesses"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.illnesses", message: "Illnesses" })}</FormLabel>
            <FormControl>
              <Textarea
                value={Array.isArray(field.value) ? (field.value as string[]).join("\n") : ""}
                onChange={(e) => {
                  const lines = e.target.value.split("\n").filter(line => line.trim() !== "");
                  field.onChange(lines.length > 0 ? lines : e.target.value === "" ? [] : [e.target.value]);
                }}
                onBlur={() => {
                  // Clean up on blur - filter empty lines
                  if (Array.isArray(field.value)) {
                    const cleaned = (field.value as string[]).filter(v => v.trim() !== "");
                    field.onChange(cleaned);
                  }
                }}
                rows={3}
                placeholder={i18n._({ id: "activities.form.illnessesPlaceholder", message: "Enter each illness on a new line" })}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.contagious"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.contagious", message: "Contagious" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectContagious", message: "Select contagious status" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="yes">{i18n._({ id: "activities.contagious.yes", message: "Yes" })}</SelectItem>
                <SelectItem value="no">{i18n._({ id: "activities.contagious.no", message: "No" })}</SelectItem>
                <SelectItem value="maybe">{i18n._({ id: "activities.contagious.maybe", message: "Maybe" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.symptoms"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.symptoms", message: "Symptoms" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.measures"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.measures", message: "Measures" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.mood"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.mood", message: "Mood" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectMood", message: "Select mood" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="happy">{i18n._({ id: "activities.mood.happy", message: "Happy" })}</SelectItem>
                <SelectItem value="ok">{i18n._({ id: "activities.mood.ok", message: "OK" })}</SelectItem>
                <SelectItem value="unhappy">{i18n._({ id: "activities.mood.unhappy", message: "Unhappy" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.child_to_be_picked_up"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{i18n._({ id: "activities.form.childToBePickedUp", message: "Child To Be Picked Up" })}</FormLabel>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.notes", message: "Notes" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function MedicationFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="data.medication_administered"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.medicationAdministered", message: "Medication Administered" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.mood"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.mood", message: "Mood" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectMood", message: "Select mood" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="happy">{i18n._({ id: "activities.mood.happy", message: "Happy" })}</SelectItem>
                <SelectItem value="ok">{i18n._({ id: "activities.mood.ok", message: "OK" })}</SelectItem>
                <SelectItem value="unhappy">{i18n._({ id: "activities.mood.unhappy", message: "Unhappy" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.child_to_be_picked_up"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{i18n._({ id: "activities.form.childToBePickedUp", message: "Child To Be Picked Up" })}</FormLabel>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.notes", message: "Notes" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function PictureFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();
  const childId = form.watch("childId");
  const currentPictureUrl = form.watch("data.picture_url") as string | undefined;

  // Generate a stable entity ID for the upload based on childId
  // This ensures uploads are associated with the child even before activity creation
  const entityId = useMemo(() => {
    return childId || crypto.randomUUID();
  }, [childId]);

  // Resolve the current picture URL if it's an image ID
  const needsResolution = isImageId(currentPictureUrl);
  const resolvedImageUrl = useImageUrl(needsResolution ? currentPictureUrl : null);
  const previewUrl = needsResolution ? resolvedImageUrl : currentPictureUrl;

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="data.picture_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>{i18n._({ id: "activities.form.picture", message: "Picture" })}</FormLabel>
            <FormControl>
              <ImageUpload
                value={field.value as string}
                onChange={(imageId) => field.onChange(imageId)}
                onRemove={() => field.onChange("")}
                purpose={images_GenerateUploadURLRequest.purpose.ATTACHMENT}
                entityType="activities"
                entityId={entityId}
                previewUrl={previewUrl || undefined}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function NoteFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="data.subject"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.subject", message: "Subject" })}</FormLabel>
            <FormControl>
              <Input {...field} value={asText(field.value)} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.content"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.content", message: "Content" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={4} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function MoodCheckFields({ form }: { form: UseFormReturn<ActivityFormInput, unknown, ActivityFormData> }) {
  const { i18n } = useLingui();

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="data.mood"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.mood", message: "Mood" })}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value as string}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={i18n._({ id: "activities.form.selectMood", message: "Select mood" })} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="happy">{i18n._({ id: "activities.mood.happy", message: "Happy" })}</SelectItem>
                <SelectItem value="ok">{i18n._({ id: "activities.mood.ok", message: "OK" })}</SelectItem>
                <SelectItem value="unhappy">{i18n._({ id: "activities.mood.unhappy", message: "Unhappy" })}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="data.notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._({ id: "activities.form.notes", message: "Notes" })}</FormLabel>
            <FormControl>
              <Textarea {...field} value={asText(field.value)} rows={3} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
